
import JSZip from "jszip";
import {structure as dbfStructure} from "dbf";
import {ExportRegistry} from "./ExportRegistry.js";
import {buildExportBasename, buildExportFilename} from "./exportFilename.js";
import {writePointZRecords, writePolyZRecords, shapefilePolygonRing, SHAPE_TYPE} from "./shapefile/geometryWriters.js";
import {encodeDbfStringValue, sanitizeFieldNames} from "./shapefile/dbfEncoding.js";

import {T, t} from "../i18n/index.js";
// Task section 7/9 audit finding: SHP was previously a StubExporter
// (disabled:true, throws if invoked) - genuinely unimplemented (see
// StubExporters.js's own history), not disabled-but-built.
//
// Task section 9: "vælg en moden eksisterende library/løsning frem for at
// implementere Shapefile-formatet manuelt". This exporter reuses two
// mature, widely-used npm packages for the genuinely complex, error-prone
// parts of a shapefile bundle: `dbf` (the DBF attribute-table binary
// structure) and `jszip` (the ZIP container). The .shp/.shx GEOMETRY
// envelope is a small, purpose-built writer (shapefile/geometryWriters.js) -
// audited alternatives (the most-used option, @mapbox/shp-write 0.4.3)
// confirmed to silently drop Z coordinates entirely (see that file's own
// header comment for the exact source-level finding), which would violate
// this task's own Z-preservation requirement, so the geometry section is
// written directly against the stable, public ESRI Shapefile Technical
// Description instead - not a reimplementation of DBF or ZIP, which stay
// fully on the mature libraries.
//
// Task section 11: features are grouped by geometry type into SEPARATE
// shapefile layers within the one ZIP (Point/LineString/Polygon) - a single
// shapefile can only ever hold one geometry type, so mixed PointView data
// has no other technically-correct representation.
const GROUPS = [
	{geometryType: "Point"},
	{geometryType: "LineString"},
	{geometryType: "Polygon"},
];

// Builds one DBF row per feature, with sanitised field names (task section
// 11: DBF's fixed 10-byte name slot) and UTF-8-safe encoded string values
// (task section 10) - numeric/boolean values are left untouched so dbf's
// own type inference (typeof-based, see dbf/src/fields.js) still assigns
// them a numeric/logical DBF field type instead of turning everything into
// a string column.
function buildRows(features, fieldNameMap){
	return features.map((f) => {
		const row = {};
		const raw = {
			FeatureID: f.id,
			Name: f.properties.name,
			Category: f.properties.category,
			Tool: f.properties.tool,
			Description: f.properties.description,
			...(f.properties.attributes || {}),
		};
		for(const [key, value] of Object.entries(raw)){
			const fieldName = fieldNameMap.get(key);
			row[fieldName] = typeof value === "string" ? encodeDbfStringValue(value) : value;
		}
		return row;
	});
}

// task section 12: SHP-export bør inkludere korrekt .prj hvis PointView
// pålideligt kender CRS - never a guessed/default one (see the confirmed
// finding that @mapbox/shp-write's own zip() silently defaults to WGS84
// geographic when no .prj is supplied, which this exporter deliberately
// does NOT do). `projection` is only treated as usable if it already looks
// like a WKT coordinate system definition (PROJCS/GEOGCS/COMPD_CS/LOCAL_CS).
// The point cloud's OWN metadata "projection" field (viewer.getProjection())
// is empty in every dataset checked (DANVA-phase finding) - still the
// fallback for callers that pass nothing, but since the Registreringer-
// model-CRS task (2026-10) the host-app resolves a real WKT per export GROUP
// from the model's own horisontal EPSG (one HTTP lookup at export time, see
// save-registrations-dialog.vue's resolveWkt()) and passes it in as
// `context.projection` - this is simply whatever the caller sends, validated
// the same way regardless of its source.
function resolveWktProjection(projection){
	if(!projection || typeof projection !== "string") return null;
	const trimmed = projection.trim();
	if(/^(PROJCS|GEOGCS|COMPD_CS|LOCAL_CS)\[/i.test(trimmed)) return trimmed;
	return null;
}

export const ShapefileFeatureExporter = {
	id: "shp",
	name: T("ESRI Shapefile"),
	extension: "zip",
	disabled: false,
	description: T("ZIP med separate lag for punkter, linjer og polygoner (med højde) og attributtabel."),
	// .prj skrives kun, når koordinatsystemet foreligger som WKT.
	crsHandling: "wkt-only",
	async export(features, context = {}){
		const zip = new JSZip();
		const wktProjection = resolveWktProjection(context.projection);
		const basename = buildExportBasename(context.pointcloudName, context.exportDate);

		const nonEmptyGroups = GROUPS
			.map((group) => ({group, groupFeatures: features.filter((f) => f.geometry.type === group.geometryType)}))
			.filter(({groupFeatures}) => groupFeatures.length > 0);

		if(nonEmptyGroups.length === 0){
			// Every PointView geometry.type is one of Point/LineString/Polygon
			// (confirmed: Feature.js only ever sets one of those three) - this
			// is unreachable in practice, kept only so an empty `features`
			// array produces a clear error instead of a silently-empty ZIP.
			throw new Error(t("Ingen eksporterbare geometrier i det valgte omfang."));
		}

		// Task section "SHP": internal files share the export's own basename
		// ("Søndergade Øst_2026-08-31_09-51-27.shp" etc.) unless mixed
		// geometry genuinely requires more than one shapefile layer (a single
		// shapefile can only ever hold one geometry type) - only then does
		// each layer get a geometry-type suffix, so the common single-
		// geometry-type export matches the task's own worked example exactly,
		// with no suffix invented where none is needed.
		const mixed = nonEmptyGroups.length > 1;

		for(const {group, groupFeatures} of nonEmptyGroups){
			const layerBaseName = mixed ? `${basename}_${group.geometryType}` : basename;

			const attributeKeys = new Set(["FeatureID", "Name", "Category", "Tool", "Description"]);
			for(const f of groupFeatures){
				for(const key of Object.keys(f.properties.attributes || {})) attributeKeys.add(key);
			}
			const fieldNameMap = sanitizeFieldNames([...attributeKeys]);

			const rows = buildRows(groupFeatures, fieldNameMap);
			const dbfView = dbfStructure(rows);

			let shp, shx;
			if(group.geometryType === "Point"){
				({shp, shx} = writePointZRecords(groupFeatures.map((f) => f.geometry.coordinates)));
			}else if(group.geometryType === "LineString"){
				({shp, shx} = writePolyZRecords(groupFeatures.map((f) => f.geometry.coordinates), SHAPE_TYPE.POLYLINEZ));
			}else{
				({shp, shx} = writePolyZRecords(groupFeatures.map((f) => shapefilePolygonRing(f.geometry.coordinates[0] || [])), SHAPE_TYPE.POLYGONZ));
			}

			zip.file(`${layerBaseName}.shp`, shp.buffer);
			zip.file(`${layerBaseName}.shx`, shx.buffer);
			zip.file(`${layerBaseName}.dbf`, dbfView.buffer);
			// task section 9/10: explicit encoding declaration, one .cpg per
			// layer (the DBF encoding is per-file, not per-archive) - QGIS and
			// other DBF-aware readers use this to decode string fields
			// correctly instead of assuming a system/legacy codepage.
			zip.file(`${layerBaseName}.cpg`, "UTF-8");
			if(wktProjection) zip.file(`${layerBaseName}.prj`, wktProjection);
		}

		const arrayBuffer = await zip.generateAsync({type: "arraybuffer", compression: "DEFLATE"});

		return {
			filename: buildExportFilename(context.pointcloudName, "zip", context.exportDate),
			blob: new Blob([arrayBuffer], {type: "application/zip"}),
			mimeType: "application/zip",
		};
	},
};

ExportRegistry.register(ShapefileFeatureExporter);
